import Image from 'next/image';
import React from 'react';

type VariantType = 'black' | 'white';

type CardProps = {
  variant?: VariantType;
  imgUrl?: string;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
};

const CARD_ICON_SIZES = '(min-width: 1024px) 48px, 36px';

const CONTAINER_BASE =
  'rounded-[20px] shadow-[0px_0px_20px_0px_rgba(0,0,0,0.04)]';

/**
 * 원본은 CardContainer 안에서 자식 styled 컴포넌트를 선택하는 중첩 CSS 였다.
 * 유틸리티는 전부 !important 라 같은 속성을 두 번 내보내면 승자가 출력 순서로 정해지므로,
 * variant 별 className 을 요소마다 정확히 하나씩만 고른다.
 * black 배경의 image-set() 은 유틸리티로 표현할 수 없어
 * globals.css 의 .sound-analytics-card-bg 로 남겼다.
 */
const CONTAINER_CLASS: Record<VariantType, string> = {
  black:
    'sound-analytics-card-bg h-[320px] bg-apps-coreGrayBlack bg-bottom bg-cover bg-no-repeat lg:h-full lg:min-h-[clamp(320px,61.667vw,740px)]',
  white: 'bg-apps-coreGrayWhite',
};

const INNER_CLASS: Record<VariantType, string> = {
  black: 'flex flex-col p-[24px] lg:p-[clamp(24px,3.125vw,60px)]',
  white:
    'flex flex-col gap-[24px] p-[24px] lg:h-full lg:justify-between lg:gap-[16px] lg:p-[clamp(24px,1.875vw,36px)]',
};

const TITLE_CONTAINER_CLASS: Record<VariantType, string> = {
  black: 'flex flex-col gap-[8px] lg:gap-[clamp(8px,2vw,24px)]',
  white: 'flex flex-col gap-[8px]',
};

const TITLE_CLASS: Record<VariantType, string> = {
  black:
    'text-[24px] font-bold leading-[140%] tracking-[-0.48px] text-apps-coreGrayWhite lg:text-[clamp(24px,3.429vw,48px)] lg:leading-[120%] lg:tracking-[-0.96px]',
  // white 는 lg 에서 line-height 를 덮지 않아 140% 가 그대로 유지된다.
  white:
    'text-[20px] font-bold leading-[140%] tracking-[-0.4px] text-apps-coreGray01 lg:text-[clamp(20px,2vw,28px)] lg:tracking-[-0.56px]',
};

const DESCRIPTION_CLASS: Record<VariantType, string> = {
  black:
    'text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray900 lg:text-[clamp(16px,1.667vw,20px)] lg:leading-[160%] lg:tracking-[-0.4px]',
  white:
    'text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-coreGray02 lg:text-[clamp(16px,1.5vw,18px)] lg:leading-[160%] lg:tracking-[-0.36px]',
};

const IMAGE_WRAPPER_CLASS =
  'relative h-[36px] w-[36px] lg:h-[clamp(36px,4vw,48px)] lg:w-[clamp(36px,4vw,48px)]';

export function SoundAnalyticsCard({
  variant = 'white',
  imgUrl,
  title,
  description,
  className,
}: CardProps) {
  return (
    <div
      className={`${CONTAINER_BASE} ${CONTAINER_CLASS[variant]} ${
        className ?? ''
      }`}
    >
      <div className={INNER_CLASS[variant]}>
        {imgUrl && (
          <div className={IMAGE_WRAPPER_CLASS}>
            {/*
              원본 StyledImage 에는 position: relative 도 있었지만
              next/image 의 fill 이 인라인으로 position: absolute 를 주기 때문에
              한 번도 적용된 적이 없다. 유틸리티는 !important 라 옮기면
              인라인을 이겨 레이아웃이 깨지므로 object-fit 만 옮긴다.
            */}
            <Image
              className="object-cover"
              src={imgUrl}
              alt="image"
              fill
              sizes={CARD_ICON_SIZES}
            />
          </div>
        )}
        <div className={TITLE_CONTAINER_CLASS[variant]}>
          <div className={TITLE_CLASS[variant]}>{title}</div>
          <div className={DESCRIPTION_CLASS[variant]}>{description}</div>
        </div>
      </div>
    </div>
  );
}
